/**
 * Shared brand/model state across the Lūneli homepage sections (hero picker,
 * case builder, giving-back picker). The selection is kept in sessionStorage
 * so it survives navigating away and back within the tab; a visitor arriving
 * fresh still has nothing selected, matching the design spec.
 */

/**
 * Canonical phone models + the collection handle that holds that model's
 * compatible products in Shopify (shells + backs, filtered further by
 * product_type client-side).
 *
 * The display name here is what the shopper sees in the picker. It does NOT
 * have to match the product option value character-for-character — matching
 * goes through window.LuneliModels.matches(), which normalises spacing, case
 * and known aliases (e.g. "iPhone Air" === "iPhone 17 Air", and a combined
 * option value like "iPhone 16e/17e" matches BOTH 16e and 17e).
 */
window.LUNELI_MODELS = window.LUNELI_MODELS || {
  iPhone: [
    ['iPhone 12', 'iphone-12'], ['iPhone 12 Pro', 'iphone-12-pro'], ['iPhone 12 Pro Max', 'iphone-12-pro-max'],
    ['iPhone 13', 'iphone-13'], ['iPhone 13 Pro', 'iphone-13-pro'], ['iPhone 13 Pro Max', 'iphone-13-pro-max'],
    ['iPhone 14', 'iphone-14'], ['iPhone 14 Plus', 'iphone-14-plus'], ['iPhone 14 Pro', 'iphone-14-pro'], ['iPhone 14 Pro Max', 'iphone-14-pro-max'],
    ['iPhone 15', 'iphone-15'], ['iPhone 15 Plus', 'iphone-15-plus'], ['iPhone 15 Pro', 'iphone-15-pro'], ['iPhone 15 Pro Max', 'iphone-15-pro-max'],
    ['iPhone 16', 'iphone-16'], ['iPhone 16 Plus', 'iphone-16-plus'], ['iPhone 16 Pro', 'iphone-16-pro'], ['iPhone 16 Pro Max', 'iphone-16-pro-max'], ['iPhone 16e', 'iphone-16e'],
    ['iPhone 17', 'iphone-17'], ['iPhone 17 Pro', 'iphone-17-pro'], ['iPhone 17 Pro Max', 'iphone-17-pro-max'], ['iPhone 17e', 'iphone-17e'],
    ['iPhone Air', 'iphone-17-air'],
    // The 18 Pro and 18 Pro Max share the 17 Pro / 17 Pro Max shells, so they
    // point at those collections rather than ones of their own.
    ['iPhone 18 Pro', 'iphone-17-pro'], ['iPhone 18 Pro Max', 'iphone-17-pro-max'],
  ],
  Samsung: [
    ['Samsung Galaxy S24', 'samsung-galaxy-24'], ['Samsung Galaxy S24+', 'samsung-galaxy-24-plus'], ['Samsung Galaxy S24 Ultra', 'samsung-galaxy-24-ultra'],
    ['Samsung Galaxy S25', 'samsung-galaxy-25'], ['Samsung Galaxy S25+', 'samsung-galaxy-25-plus'], ['Samsung Galaxy S25 Ultra', 'samsung-galaxy-25-ultra'],
    ['Samsung Galaxy S26', 'samsung-galaxy-24-copy'], ['Samsung Galaxy S26+', 'samsung-galaxy-26-copy'], ['Samsung Galaxy S26 Ultra', 'samsung-galaxy-26-plus-copy'],
  ],
};
window.LUNELI_SHELL_COLLECTION = { iPhone: 'iphone-shells', Samsung: 'samsung-shells' };

/**
 * Model-name matching. Product option values across the catalogue are not
 * consistent ("iPhone 16e" vs "iPhone 16 e", "iPhone Air" vs "iPhone 17 Air",
 * "iPhone 16e/17e" covering two models in one value), so every comparison
 * between a shopper's selected model and a variant goes through here.
 */
window.LuneliModels = window.LuneliModels || (function () {
  // Values that mean the same physical phone under different names.
  var ALIASES = {
    iphone17air: 'iphoneair',
    iphoneair17: 'iphoneair',
    // Pre-migration Samsung spellings, kept so any stale cached value or
    // hand-typed URL still resolves to the canonical phone.
    samsunggalaxy24: 'samsunggalaxys24',
    samsunggalaxy24plus: 'samsunggalaxys24plus',
    samsunggalaxy24ultra: 'samsunggalaxys24ultra',
    samsunggalaxy25: 'samsunggalaxys25',
    samsunggalaxy25plus: 'samsunggalaxys25plus',
    samsunggalaxy25ultra: 'samsunggalaxys25ultra',
    samsunggalaxy26: 'samsunggalaxys26',
    samsunggalaxy26plus: 'samsunggalaxys26plus',
    samsunggalaxy26ultra: 'samsunggalaxys26ultra',
    galaxys24: 'samsunggalaxys24',
    galaxys24plus: 'samsunggalaxys24plus',
    galaxys24ultra: 'samsunggalaxys24ultra',
    galaxys25: 'samsunggalaxys25',
    galaxys25plus: 'samsunggalaxys25plus',
    galaxys25ultra: 'samsunggalaxys25ultra',
    galaxys26: 'samsunggalaxys26',
    galaxys26plus: 'samsunggalaxys26plus',
    galaxys26ultra: 'samsunggalaxys26ultra',
  };

  function norm(value) {
    // "+" carries meaning here — Galaxy S24 and Galaxy S24+ are different
    // phones — so spell it out before stripping punctuation, otherwise both
    // collapse to the same key and the wrong variant gets matched.
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/\+/g, 'plus')
      .replace(/[^a-z0-9]/g, '');
  }

  function canon(value) {
    var key = norm(value);
    return ALIASES[key] || key;
  }

  /**
   * One option value can cover several models ("iPhone 16e/17e"). Returns
   * every canonical key that value should match.
   */
  function keysFor(optionValue) {
    var raw = String(optionValue == null ? '' : optionValue);
    var parts = raw.split('/').map(function (p) { return p.trim(); }).filter(Boolean);
    if (parts.length < 2) return [canon(raw)];

    // "iPhone 16e/17e" -> lead word "iPhone", so "17e" becomes "iPhone 17e".
    var leadMatch = parts[0].match(/^([A-Za-z]+(?:\s+[A-Za-z]+)*)/);
    var lead = leadMatch ? leadMatch[0].trim() : '';
    return parts.map(function (part, i) {
      if (i === 0) return canon(part);
      if (lead && !/^[A-Za-z]/.test(part)) return canon(lead + ' ' + part);
      return canon(part);
    });
  }

  return {
    norm: norm,
    canon: canon,
    keysFor: keysFor,
    /** Does this product-option value refer to the selected model? */
    matches: function (optionValue, selectedModel) {
      if (!optionValue || !selectedModel) return false;
      return keysFor(optionValue).indexOf(canon(selectedModel)) !== -1;
    },
    /** Is this string one of the model names we know about? */
    isModelName: function (value) {
      var target = canon(value);
      var all = (window.LUNELI_MODELS.iPhone || []).concat(window.LUNELI_MODELS.Samsung || []);
      return all.some(function (m) { return canon(m[0]) === target; });
    },
    /** The collection handle for a brand + model pair. */
    collectionHandle: function (brand, model) {
      var list = window.LUNELI_MODELS[brand] || [];
      var target = canon(model);
      var hit = list.find(function (m) { return canon(m[0]) === target; });
      return hit ? hit[1] : null;
    },
  };
})();

(function () {
  if (window.LuneliStore) return;

  /**
   * The chosen phone survives navigation within the tab. Leaving the builder
   * for a product page and coming back used to drop the shopper on an unpicked
   * homepage, which reads as "I lost my place". sessionStorage is the right
   * scope: it follows the tab, including back/forward, and expires with it —
   * a first-time visitor still arrives with nothing selected, as designed.
   */
  var STORAGE_KEY = 'luneli:picker';

  function readSaved() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var saved = JSON.parse(raw);
      if (!saved || !saved.brand || !window.LUNELI_MODELS[saved.brand]) return null;
      // Never restore a model that no longer exists in the catalogue.
      var known = (window.LUNELI_MODELS[saved.brand] || []).some(function (pair) {
        return pair[0] === saved.model;
      });
      return { brand: saved.brand, model: known ? saved.model : '' };
    } catch (e) {
      return null; // private mode, storage disabled — carry on without it
    }
  }

  function persist(next) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      /* not worth breaking the picker over */
    }
  }

  var state = readSaved() || { brand: 'iPhone', model: '' };
  var listeners = [];

  window.LuneliStore = {
    get: function () {
      return { brand: state.brand, model: state.model };
    },
    set: function (partial) {
      state = Object.assign({}, state, partial);
      persist(state);
      listeners.forEach(function (fn) {
        fn(window.LuneliStore.get());
      });
    },
    subscribe: function (fn) {
      listeners.push(fn);
      return function unsubscribe() {
        listeners = listeners.filter(function (l) { return l !== fn; });
      };
    },
  };
})();

/**
 * Shared helpers for talking to the theme's real cart (Ajax Cart API) and
 * keeping the existing header cart badge / cart-drawer in sync, mirroring
 * the pattern used by assets/products-bundle.js.
 */
window.LuneliCart = window.LuneliCart || {
  /**
   * Money formatting. Shopify's two product endpoints disagree about price
   * units: /products/{handle}.js returns integer cents (1499), while
   * /collections/{handle}/products.json returns a decimal string ("14.99").
   * toCents() normalises both; money() always takes cents.
   */
  toCents: function (value) {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Math.round(value);
    var parsed = parseFloat(value);
    return isNaN(parsed) ? null : Math.round(parsed * 100);
  },

  money: function (cents) {
    if (cents === null || cents === undefined || isNaN(cents)) return '';
    return '$' + (cents / 100).toFixed(2);
  },

  /** The theme's root route, with a safe fallback if FoxThemeSettings is late. */
  root: function () {
    var settings = window.FoxThemeSettings;
    if (settings && settings.routes && settings.routes.root) return settings.routes.root;
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  },

  route: function (name, fallback) {
    var settings = window.FoxThemeSettings;
    if (settings && settings.routes && settings.routes[name]) return settings.routes[name];
    return window.LuneliCart.root().replace(/\/$/, '') + fallback;
  },

  getState: function () {
    return fetch(window.LuneliCart.root() + 'cart.js', {
      headers: { Accept: 'application/json' },
    }).then(function (r) { return r.json(); });
  },

  /**
   * `preview` is optional: {title, variantTitle, price(cents), image}. When it
   * is supplied the page is told about the add BEFORE the network round trip,
   * so any Lūneli cart UI can draw the new line on the same frame as the click.
   */
  add: function (variantId, quantity, preview) {
    quantity = quantity || 1;
    if (preview) {
      document.dispatchEvent(
        new CustomEvent('luneli:cart-optimistic', {
          detail: { variantId: variantId, quantity: quantity, preview: preview },
        })
      );
    }
    var sections = [];
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.getSectionsToRender) {
      sections = cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
    }
    return fetch(window.LuneliCart.route('cart_add_url', '/cart/add'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: quantity }],
        sections: sections,
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) {
          throw new Error(response.description || 'Could not add to cart');
        }
        if (window.FoxThemeEvents && window.PUB_SUB_EVENTS) {
          window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, response);
        }
        // The theme's own pub/sub is not guaranteed to be on the page. Without
        // a channel of our own, a section that added to cart could not tell the
        // builder's collection rail about it, and the rail sat unchanged until
        // something else was added from inside the builder itself.
        document.dispatchEvent(new CustomEvent('luneli:cart-updated', { detail: response }));
        return response;
      })
      .catch(function (err) {
        // Resync so an optimistic line drawn above does not linger after a
        // failed add.
        document.dispatchEvent(new CustomEvent('luneli:cart-updated', { detail: null }));
        throw err;
      });
  },

  changeLine: function (line, quantity) {
    return fetch(window.LuneliCart.route('cart_change_url', '/cart/change'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        if (window.FoxThemeEvents && window.PUB_SUB_EVENTS) {
          window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, state);
        }
        document.dispatchEvent(new CustomEvent('luneli:cart-updated', { detail: state }));
        return state;
      });
  },
};

/**
 * Normalises a product from EITHER Shopify product endpoint into one shape:
 *   { id, handle, title, product_type, tags, description,
 *     images: [srcString], variants: [{ id, title, price(cents),
 *     compare_at_price(cents), available, image }] }
 *
 * This exists because /collections/{handle}/products.json and
 * /products/{handle}.js return different shapes for price (string vs cents),
 * images (objects vs strings) and description (body_html vs description).
 */
window.LuneliProducts = window.LuneliProducts || {
  normalize: function (product) {
    if (!product) return null;
    var toCents = window.LuneliCart.toCents;
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      product_type: product.product_type || product.type || '',
      tags: product.tags || [],
      description: product.body_html || product.description || '',
      images: (product.images || [])
        .map(function (image) {
          if (!image) return '';
          return typeof image === 'string' ? image : image.src || '';
        })
        .filter(Boolean),
      variants: (product.variants || []).map(function (variant) {
        return {
          id: variant.id,
          title: variant.title || '',
          price: toCents(variant.price),
          compare_at_price: toCents(variant.compare_at_price),
          available: variant.available !== false,
          image: (variant.featured_image && variant.featured_image.src) || null,
        };
      }),
    };
  },

  /** The model half of a variant title ("iPhone 15 Pro / Black" -> "iPhone 15 Pro"). */
  variantModel: function (variant) {
    return String(variant.title || '').split(' / ')[0];
  },

  /** The colour half, or null when the product has no colour option. */
  variantColor: function (variant) {
    var parts = String(variant.title || '').split(' / ');
    return parts.length > 1 ? parts.slice(1).join(' / ') : null;
  },
};
